/**
 * StudentTutoringTwoPane — « Messages » et, au large, la conversation ouverte à côté
 * (`<StudentCoachTwoPane>`).
 *
 * L'onglet de « Messages » le pose à la place de sa page. La disposition se tranche sur la
 * largeur de l'onglet, au seuil du gabarit (`TWO_PANE_FROM`), lue comme lui la lit (le conteneur,
 * jamais la fenêtre) :
 *  - sur un téléphone, rien ne change : la page seule, et le toucher d'un tuteur pousse `messenger`
 *    (`onOpenMessagesOnPhone`, le geste de l'onglet) ;
 *  - au large, toucher un tuteur ouvre SA conversation dans le volet droit, sans retour, et marque
 *    sa ligne ; rien d'ouvert, le volet le dit. La conversation n'est montée (lue, marquée lue)
 *    que quand elle se voit.
 */
import React, { useCallback, useState } from "react"
import { type LayoutChangeEvent, StyleSheet, View } from "react-native"

import { StudentCoachTwoPane, TWO_PANE_FROM, type StudentCoachPageProps } from "../../../../../../design-system/student"
import { conversationTutorId } from "../../../../adapters/student/Messaging/studentConversationAdapter"
import { type UITutor } from "../../../types"
import { StudentTutorConversationOverlays } from "./StudentTutorConversationOverlays"
import { useStudentTutorConversation } from "./useStudentTutorConversation"

export type StudentTutoringTwoPaneProps = {
    /** « Messages », tel que l'onglet le pose — sauf `onOpenMessages`, que ce volet décide. */
    coach: Omit<StudentCoachPageProps, "onOpenMessages">
    /** Le tuteur principal : sa conversation est celle de la route sans paramètre. */
    mainTutorId: UITutor["id"] | null
    /** Le geste de l'onglet sur un téléphone : pousser `messenger`. */
    onOpenMessagesOnPhone: (tutor: UITutor) => void
}

export function StudentTutoringTwoPane(props: StudentTutoringTwoPaneProps) {
    const { coach, mainTutorId, onOpenMessagesOnPhone } = props
    const [split, setSplit] = useState(false)
    const [openTutorId, setOpenTutorId] = useState<UITutor["id"] | null>(null)

    const onLayout = useCallback(
        (event: LayoutChangeEvent) => setSplit(event.nativeEvent.layout.width >= TWO_PANE_FROM),
        []
    )
    const open = split && openTutorId != null
    const { page, overlays } = useStudentTutorConversation({
        tutorId: openTutorId != null ? conversationTutorId(openTutorId, mainTutorId) : null,
        embedded: true,
        active: open,
    })

    const onOpenMessages = (tutor: UITutor) => (split ? setOpenTutorId(tutor.id) : onOpenMessagesOnPhone(tutor))

    return (
        <View style={styles.root} onLayout={onLayout}>
            <StudentCoachTwoPane
                coach={{ ...coach, onOpenMessages }}
                conversation={open ? { ...page, onBack: () => setOpenTutorId(null) } : null}
            />
            {open && <StudentTutorConversationOverlays {...overlays} />}
        </View>
    )
}

const styles = StyleSheet.create({ root: { flex: 1 } })
